import { useMemo, useState } from 'react'
import { useIntl } from "umi";
import SummaryValues from '@/pages/dashboard/components/SummaryValues'
import { formatNum, formatPercentNum, handleDownload, unitConversion } from '@/pages/dashboard/utils/utils'
import { useLocal, useWeekDay } from '@/pages/dashboard/utils/customHook'

import useOptions from './hooks/useOptions'
import useSelectorDataNew from '@/pages/dashboard/filterPageComponents/multiTimezone/hooks/useSelectorDataNew'
import CardBox from '@/pages/dashboard/components/CardBox'
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart'
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart'
import { formatDataByChartType } from '@/pages/dashboard/components/Chart/_config/plug';

import './index.scss'


interface MetricsTrendCardProps {
  data?: any
  loading?: boolean
  formValue?: any
  selectMetrics?: any
  error?: boolean
}

export default function Country(props: MetricsTrendCardProps) {
  const {
    data,
    loading,
    error,
    formValue,
    selectMetrics
  } = props

  const { isEN, getLocalText } = useLocal()
  const { selectorData } = useSelectorDataNew("country")
  const type = (() => {
    return formValue?.region?.markets?.length > 1 ? 'multiple' : 'single'
  })()

  const { formatMessage } = useIntl();
  const { getWeekDayStr } = useWeekDay()

  const [iseye, setIseye] = useState(true)
  

  // #region 处理国家源数据
  const countryDataFilter = (data, type) => {
    // 多国家逻辑
    if (type === 'multiple') {
      // 根据国家简写建立数据映射
      const legendMap = {}
      data?.metrics_list?.forEach(item => {
        const { country } = item
        if (!legendMap[country]) {
          legendMap[country] = []
        }
        legendMap[country].push(item)
      })
      // 取到筛选器中的国家对象
      const countryRenderList = (() => {
        let result = selectorData?.country_list?.filter(item => formValue?.region?.markets?.includes(item?.country_code))

        return data?.top?.map(metricsKey => {
          return result?.filter(item => item?.country_code === metricsKey)[0]
        })
      })()
      const summaryMap = {}
      data?.aggregation?.forEach(item => {
        const { country } = item
        if (!summaryMap[country]) {
          summaryMap[country] = []
        }
        summaryMap[country].push(item)
      })

      return {
        chartData: countryRenderList?.map(item => {
          return {
            name: (isEN ? item?.country_name_en : item?.country_name_ch) + ` ${item?.deviation ? `(${item?.deviation})` : ''}`,
            data: legendMap[item?.country_code]?.map(dataItem => {
              return ({
                x: dataItem.date,
                y: dataItem?.metrics?.[selectMetrics?.value]?.value
              })
            })
          }
        }),
        summaryData: countryRenderList?.map(item => {
          const sum = summaryMap[item?.country_code]?.filter(item => item?.type === 'sum')?.[0]?.metrics?.[selectMetrics?.value]?.value
          const avg = summaryMap[item?.country_code]?.filter(item => item?.type === 'avg_no_null')?.[0]?.metrics?.[selectMetrics?.value]?.value

          const formatCallback = (value) => {
            if (selectMetrics?.isPercent) {
              return formatPercentNum(value, selectMetrics?.decimals)
            } else {
              return formatNum(value, selectMetrics?.isDollar, selectMetrics?.isUserType)
            }
          }

          return {
            name: (isEN ? item?.country_name_en : item?.country_name_ch) + ` ${item?.deviation ? `(${item?.deviation})` : ''}`,
            sum: `${getLocalText('daily_by_time_zone_trend_sum')} : ` + formatCallback(sum),
            avg: `${getLocalText('daily_by_time_zone_trend_avg')} : ` + formatCallback(avg),
          }
        })
      }
    } else {
      // 单国家逻辑

      // 接口反参字段
      const chartData = ['metrics_list', 'sdlw_metrics_list']
      const summaryData = ['avg_sum_metrics', 'sdlw_avg_sum_metrics']
      // 当前选中的国家
      const target = selectorData?.countryList?.filter(item => {
        return item?.country_code === formValue?.region?.markets?.[0]
      })?.[0]
      const countryName = `${target?.[`country_name_${isEN ? 'en' : 'ch'}`]} (${target?.deviation})`
      const nameArr = [
        target?.country_code === '255' || !target ? `${getLocalText('daily_by_time_zone_selector_total')}` : countryName,
        getLocalText('daily_by_time_zone_trend_sdlw')
      ]
      // common_sdlw
      const xAxis1 = data?.[chartData[0]]?.map(item => item?.date)
      const xAxis2 = data?.[chartData[1]]?.map(item => item?.date)

      return {
        xAxis1,
        xAxis2,
        chartData: chartData?.map((key, index) => {
          return {
            name: nameArr[index],
            data: data?.[key]?.map(item => ({
              x: item?.date,
              y: item?.metrics?.[selectMetrics?.value]?.value
            }))
          }
        }),
        summaryData: summaryData?.map((key, index) => {
          const sum = data?.[key]?.[0]?.metrics?.[selectMetrics?.value]?.value
          const avg = data?.[key]?.[1]?.metrics?.[selectMetrics?.value]?.value
          const formatCallback = (value) => {
            if (selectMetrics?.isPercent) {
              return formatPercentNum(value, selectMetrics?.decimals)
            } else {
              return formatNum(value, selectMetrics?.isDollar, selectMetrics?.isUserType)
            }
          }
          return {
            name: nameArr[index],
            sum: `${getLocalText('daily_by_time_zone_trend_sum')} : ` + formatCallback(sum),
            avg: `${getLocalText('daily_by_time_zone_trend_avg')} : ` + formatCallback(avg),
          }
        })
      }
    }
  }
  // #endregion

  // #region 图表配置
  const { xAxis1, xAxis2, chartData, summaryData } = useMemo(() => {
    return countryDataFilter(data, type)
  }, [selectMetrics?.value, data, type, isEN])
  const {
    config,
    options
  } = useOptions({
    data: {
      xAxis1,
      xAxis2,
      chartData
    },
    type,
    iseye,
    selectMetrics
  })
  const lineChartConfig = formatDataByChartType('line', options?.chart, config[type]?.line)
  const barChartConfig = formatDataByChartType('bar', options?.chart, config[type]?.bar)

  const isHaveData = useMemo(()=>{
    return barChartConfig?.series?.map(((item:any) =>{
      return item?.data?.filter((items:any)=>{
        return items !== ''
      }) || []
      
    }))?.flat() || [];
  },[barChartConfig?.series])

  const isHaveDataFlag = isHaveData?.length>0;
  

  const renderList = [
    type === 'single' &&  {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: "common_bar_chart" }),
      component:
        <BarChart
          height={307}
          title={''}
          legendData={barChartConfig?.legend || []}
          xAxisData={barChartConfig?.xAxisData || []}
          series={barChartConfig?.series || []}
          formatterFunc={(params: any) => config[type].bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config[type].bar.setOtherConfigFunc(opt)}
        />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: "common_line_chart" }),
      component:
        <LineChart
          height={307}
          title={''}
          legendData={lineChartConfig?.legend || []}
          xAxisData={lineChartConfig?.xAxisData || []}
          series={lineChartConfig?.series || []}
          formatterFunc={(params: any) => config[type].bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config[type].bar.setOtherConfigFunc(opt)}
        />
      ,
    },
  ]

  return (
    <CardBox
      className='register-table'
      height={307}
      titleCom={isHaveDataFlag && <div className='dbd-time-zone-chart-title'>{selectMetrics?.text}</div>}
      middleContentCom={<><SummaryValues summaryList={summaryData} /></>}
      renderList={renderList}
      defaultAvtive={type === 'single' ? 2 : 1}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye)
        }
      }}
      bordered={false}
      isLoading={loading}
      isError={error}
      isNodata={!isHaveDataFlag }
    />

  )
}
